import { useState } from 'react'
import { getServerSessionId } from '../../lib/serverSession'

interface PaymentDiscrepancy {
  type: 'missing_shares' | 'missing_commission' | 'incorrect_commission' | 'orphaned_shares' | 'shares_without_payment' | 'user_missing_all_shares'
  severity: 'critical' | 'warning' | 'info'
  payment_id?: string
  share_purchase_id?: number
  user_id: number
  username: string
  email: string
  amount: number
  expected_shares: number
  actual_shares: number
  expected_usdt_commission?: number
  actual_usdt_commission?: number
  expected_share_commission?: number
  actual_share_commission?: number
  sponsor_username?: string
  payment_date: string
  description: string
  fix_action: string
}

interface AuditSummary {
  total_approved_payments: number
  payments_with_shares: number
  payments_missing_shares: number
  total_share_purchases: number
  shares_without_payment: number
  users_missing_all_shares: number
  total_discrepancies: number
  critical_issues: number
  total_affected_amount: number
}

export default function PaymentIntegrityAudit() {
  const [_loading, _setLoading] = useState(false)
  const [auditing, setAuditing] = useState(false)
  const [discrepancies, setDiscrepancies] = useState<PaymentDiscrepancy[]>([])
  const [summary, setSummary] = useState<AuditSummary | null>(null)
  const [filter, setFilter] = useState<'all' | 'critical' | 'warning'>('all')
  const [dateRange, setDateRange] = useState({ start: '', end: '' })
  const [fixing, setFixing] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  const runAudit = async () => {
    setAuditing(true)
    setMessage(null)
    setDiscrepancies([])
    setSummary(null)

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-integrity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'run_audit_v1', data: { dateRange } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      setDiscrepancies(result.discrepancies)
      setSummary(result.summary)

      if (result.discrepancies.length === 0) {
        setMessage({ type: 'success', text: '✅ No discrepancies found! All payments have correct share allocations and commissions.' })
      } else {
        setMessage({ type: 'error', text: `⚠️ Found ${result.discrepancies.length} discrepancies (${result.discrepancies.filter((i: any) => i.severity === 'critical').length} critical)` })
      }
    } catch (error: any) {
      console.error('Error running audit:', error)
      setMessage({ type: 'error', text: `Audit failed: ${error.message}` })
    } finally {
      setAuditing(false)
    }
  }

  const fixDiscrepancy = async (discrepancy: PaymentDiscrepancy) => {
    if (!discrepancy.payment_id) {
      setMessage({ type: 'error', text: 'Cannot fix: No payment ID' })
      return
    }

    setFixing(discrepancy.payment_id)
    setMessage(null)

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-integrity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'fix_discrepancy', data: { discrepancy } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      setMessage({
        type: 'success',
        text: `✅ Fixed! Created share purchase record for ${discrepancy.username} (${discrepancy.expected_shares} shares)`
      })

      // Re-run audit
      await runAudit()
    } catch (error: any) {
      console.error('Error fixing discrepancy:', error)
      setMessage({ type: 'error', text: `Fix failed: ${error.message}` })
    } finally {
      setFixing(null)
    }
  }

  const exportToCSV = () => {
    const filtered = filter === 'all' ? discrepancies : discrepancies.filter(d => d.severity === filter)
    
    const csv = [
      ['Type', 'Severity', 'User', 'Email', 'Amount', 'Expected Shares', 'Actual Shares', 'Date', 'Description'].join(','),
      ...filtered.map(d => [
        d.type,
        d.severity,
        d.username,
        d.email,
        d.amount,
        d.expected_shares,
        d.actual_shares,
        d.payment_date,
        `"${d.description}"`
      ].join(','))
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `payment-integrity-audit-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
  }

  const filteredDiscrepancies = filter === 'all' 
    ? discrepancies 
    : discrepancies.filter(d => d.severity === filter)

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">🔍 Payment Integrity Audit</h2>
        <p className="text-white">
          Comprehensive audit of all approved payments to verify share allocations and commission payments
        </p>
      </div>

      {/* Controls */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-300 mb-2">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-300 mb-2">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
          </div>
          <button
            onClick={runAudit}
            disabled={auditing}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {auditing ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Running Audit...
              </span>
            ) : (
              '🔍 Run Audit'
            )}
          </button>
          {discrepancies.length > 0 && (
            <button
              onClick={exportToCSV}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
            >
              📥 Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'}`}>
          {message.text}
        </div>
      )}

      {/* Summary */}
      {summary && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-500/30">
              <div className="text-blue-300 text-sm mb-1 font-medium">📊 Total Payments</div>
              <div className="text-3xl font-bold text-white">{summary.total_approved_payments}</div>
              <div className="text-blue-400 text-sm mt-1">Approved crypto payments</div>
            </div>
            <div className="bg-purple-900/30 rounded-lg p-4 border border-purple-500/30">
              <div className="text-purple-300 text-sm mb-1 font-medium">📈 Total Share Purchases</div>
              <div className="text-3xl font-bold text-white">{summary.total_share_purchases}</div>
              <div className="text-purple-400 text-sm mt-1">Active share records</div>
            </div>
            <div className="bg-red-900/30 rounded-lg p-4 border border-red-500/30">
              <div className="text-red-300 text-sm mb-1 font-medium">🚨 Critical Issues</div>
              <div className="text-3xl font-bold text-red-400">{summary.critical_issues}</div>
              <div className="text-red-400 text-sm mt-1">Require immediate action</div>
            </div>
            <div className="bg-orange-900/30 rounded-lg p-4 border border-orange-500/30">
              <div className="text-orange-300 text-sm mb-1 font-medium">💰 Affected Amount</div>
              <div className="text-3xl font-bold text-white">${summary.total_affected_amount.toFixed(2)}</div>
              <div className="text-orange-400 text-sm mt-1">Total value at risk</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-green-900/30 rounded-lg p-4">
              <div className="text-white text-sm mb-1">✅ Payments With Shares</div>
              <div className="text-2xl font-bold text-green-400">{summary.payments_with_shares}</div>
            </div>
            <div className="bg-red-900/30 rounded-lg p-4">
              <div className="text-white text-sm mb-1">❌ Payments Missing Shares</div>
              <div className="text-2xl font-bold text-red-400">{summary.payments_missing_shares}</div>
            </div>
            <div className="bg-orange-900/30 rounded-lg p-4">
              <div className="text-white text-sm mb-1">⚠️ Shares Without Payment</div>
              <div className="text-2xl font-bold text-orange-400">{summary.shares_without_payment}</div>
            </div>
            <div className="bg-red-900/30 rounded-lg p-4">
              <div className="text-white text-sm mb-1">🚨 Users Missing ALL Shares</div>
              <div className="text-2xl font-bold text-red-400">{summary.users_missing_all_shares}</div>
            </div>
          </div>
        </>
      )}

      {/* Filters */}
      {discrepancies.length > 0 && (
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
          >
            All ({discrepancies.length})
          </button>
          <button
            onClick={() => setFilter('critical')}
            className={`px-4 py-2 rounded-lg font-medium ${filter === 'critical' ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300'}`}
          >
            Critical ({discrepancies.filter(d => d.severity === 'critical').length})
          </button>
          <button
            onClick={() => setFilter('warning')}
            className={`px-4 py-2 rounded-lg font-medium ${filter === 'warning' ? 'bg-yellow-600 text-white' : 'bg-gray-700 text-gray-300'}`}
          >
            Warnings ({discrepancies.filter(d => d.severity === 'warning').length})
          </button>
        </div>
      )}

      {/* Discrepancies Table */}
      {filteredDiscrepancies.length > 0 && (
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Severity</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">User</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Expected</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Actual</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Issue</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredDiscrepancies.map((disc, idx) => (
                  <tr key={idx} className="hover:bg-gray-750">
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        disc.severity === 'critical' ? 'bg-red-900/50 text-red-200' : 'bg-yellow-900/50 text-yellow-200'
                      }`}>
                        {disc.severity.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{disc.username}</div>
                      <div className="text-white text-sm">{disc.email}</div>
                    </td>
                    <td className="px-4 py-3 text-white">${disc.amount.toFixed(2)}</td>
                    <td className="px-4 py-3 text-white">{disc.expected_shares} shares</td>
                    <td className="px-4 py-3 text-white">{disc.actual_shares} shares</td>
                    <td className="px-4 py-3 text-gray-300 text-sm max-w-md">{disc.description}</td>
                    <td className="px-4 py-3">
                      {disc.type === 'missing_shares' && disc.payment_id && (
                        <button
                          onClick={() => fixDiscrepancy(disc)}
                          disabled={fixing === disc.payment_id}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium disabled:opacity-50"
                        >
                          {fixing === disc.payment_id ? 'Fixing...' : '🔧 Fix'}
                        </button>
                      )}
                      {disc.type === 'shares_without_payment' && (
                        <div className="text-orange-400 text-sm">
                          ⚠️ Manual Investigation Required
                        </div>
                      )}
                      {disc.type === 'user_missing_all_shares' && (
                        <div className="text-red-400 text-sm">
                          🚨 Contact User Immediately
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

